// istanbul ignore file
// TODO: DASH - this has been copied from Shopper Platform - it may be better to have this in a shared library?

// libraries
import { formatUtils, vehicleUtils } from '@makemydeal/dr-common-utils';
import { stripHtmlTags } from '@makemydeal/dr-dash-ui-utils';
import { BuildPayloadPurpose } from '@makemydeal/dr-dash-bff-types';
import {
    getPaymentGridSaveData,
    incentiveSelectors,
    paymentPayloadUtils,
    utils as offerReduxUtils
} from '@makemydeal/dr-offer-redux';
import { BALLOON, CASH, FINANCE, LEASE, OdometerMileageTypes, PaymentScheduleTypes } from '@makemydeal/dr-platform-types';
import { dealerSelectors, featureToggleSelectors, selectedOfferSelectors } from '@makemydeal/dr-shared-store';
import { resolveCellPhone } from '@makemydeal/dr-shared-ui-utils/phone/resolvePhone';

// selectors
import * as accessoriesSelectors from '../selectors/accessoriesSelectors';
import * as baggageFuelEconomySelectors from '../selectors/baggageFuelEconomySelectors';
import * as baseDealSelectors from '../selectors/BaseDealSelectors';
import * as bridgeUserSelectors from '../selectors/bridgeUserSelectors';
import * as compositeSelectors from '../selectors/compositeSelectors';
import * as connectionSelectors from '../selectors/connectionSelectors';
import * as createDealSelectors from '../selectors/createDealSelectors';
import * as creditAppSelectors from '../selectors/creditAppSelectors';
import * as dealLifecycleSelectors from '../selectors/dealLifecycleSelectors';
import * as dealLockingSelectors from '../selectors/dealLockingSelectors';
import * as dealModeSelectors from '../selectors/dealModeSelectors';
import * as dealTemplateSelectors from '../selectors/dealTemplateSelectors';
import * as dealXgDetailsSelectors from '../selectors/dealXgDetailsSelectors';
import * as deskingSelectors from '../selectors/deskingSelectors';
import * as leadFormInfoSelectors from '../selectors/leadFormInfoSelectors';
import * as leadSelectors from '../selectors/leadSelectors';
import * as metaDataSelectors from '../selectors/metaDataSelectors';
import * as offerInfoSelectors from '../selectors/offerInfoSelectors';
import * as manualFieldOverridesSelectors from '../selectors/manualFieldOverridesSelectors';
import * as offerReduxSelectors from '../selectors/offerRedux';
import * as offerSelectors from '../selectors/offerSelectors';
import * as programsSelectors from '../selectors/programsSelectors';
import * as pushToDmsSelectors from '../selectors/pushToDmsSelectors';
import * as tradeInSelectors from '../selectors/trade';
import * as vdpSourceSelectors from '../selectors/vdpSourceSelectors';
import * as vehicleSelectors from '../selectors/vehicleSelectors';
import * as widgetSettingsSelectors from '../selectors/widgetSettingsSelectors';

// interfaces/types
import type { ITradeInState as TradeInState } from '@makemydeal/dr-activities-common';
import { MV_AUTO_SELECT_PROGRAM_SAVED_BAGGAGE_NAME } from '@makemydeal/dr-dash-types';
import type { StateTree } from '@makemydeal/dr-dash-types';
import type {
    AutoSelectStatePayload,
    BaggageEntry,
    ISaveOffer,
    LeaseResidualOverridePayload,
    MvDealStatePayload,
    InitPayloadOffer,
    Lead,
    SaveOfferPayload,
    FinancingTerms
} from '@makemydeal/dr-dash-bff-types';
import type { paymentTypes } from '@makemydeal/dr-offer-redux';
import { type IDealSummaryItem as DealSummaryItem, type IPaymentTerm as PaymentTerm } from '@makemydeal/dr-platform-shared';
import type {
    CoBuyer,
    FlexBuyPayment,
    GenericObject,
    IPencilOfferInputs,
    IShopper,
    ITradeIn,
    IVehicle,
    Offer,
    OfferType,
    SaveOfferDealer,
    SaveOfferMenuProductsPayload
} from '@makemydeal/dr-platform-types';
import type { Dealer } from '@makemydeal/dr-shared-types';

import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';
import { createCalculationDetailsForOfferSave, getPartnerCode, getPartnerDealerId } from '../selectors/calculationDetailsSelectors';
import { getTradeIn } from '../selectors/trade';
import { getIncludedTradeInItems, isTradeInsArray } from '../selectors/tradeIns';

import { DealMode } from '../types/dealModeEnum';
import { buildEmployeeDetails } from './employeeUtils';
import { buildIncentivesNode } from './incentivesUtil';
import { AccessoriesAdapter } from './manualAccessoriesAdapter';
import { toOfferTypeSpecificFormat } from './manualAccessoriesUtils';
import { createMenuPayload } from './menuPayloadUtil';
import { createTradesPayload } from './tradesPayloadUtil';
import * as paymentFrequencySelectors from '../selectors/paymentFrequencySelectors';
import { omit } from 'lodash';
import { getVppDarwinBaselinePayload } from '../selectors/vehicleProtectionSelectors';

// TODO: DASH - move this to the credit reducer when it has been added

const isLeadEmpty = (lead: Lead | null) => {
    // no reason to import lodash when native can do the same thing
    return (lead && Object.keys(lead).length === 0 && Object.getPrototypeOf(lead) === Object.prototype) || !lead?.id;
};

export const addMvAutoSelectProgramSavedBaggage = (existingBaggage: BaggageEntry[] = []): BaggageEntry[] => {
    const withoutMarker = existingBaggage.filter(({ name }) => name !== MV_AUTO_SELECT_PROGRAM_SAVED_BAGGAGE_NAME);
    return [
        ...withoutMarker,
        {
            name: MV_AUTO_SELECT_PROGRAM_SAVED_BAGGAGE_NAME,
            value: { type: 'String', content: new Date().toISOString() }
        }
    ];
};

// TODO: DASH - define payload type below

const editedIdsFor = (state: StateTree, offerType: OfferType): Set<number> =>
    offerReduxUtils.incentives.isGridIncentiveManagementEnabled(state, offerType)
        ? incentiveSelectors.getEditedIncentiveIds(state, offerType)
        : new Set<number>();

const withoutIds = <T extends { appliedIncentiveIds?: number[] } | undefined>(record: T, ids: Set<number>): T =>
    ids.size && record?.appliedIncentiveIds?.some((id) => ids.has(id))
        ? { ...record, appliedIncentiveIds: record.appliedIncentiveIds.filter((id) => !ids.has(id)) }
        : record;

export const withoutEditedAppliedIds = <T extends { appliedIncentiveIds?: number[] } | undefined>(
    record: T,
    state: StateTree,
    offerType: OfferType
): T => (record?.appliedIncentiveIds?.length ? withoutIds(record, editedIdsFor(state, offerType)) : record);

export const withoutEditedAppliedIdsByTerm = (paymentData: Record<string, any> | undefined, state: StateTree) => {
    if (!paymentData) {
        return paymentData;
    }
    const editedByOfferType = new Map<string, Set<number>>();
    const editedFor = (offerType: OfferType): Set<number> => {
        if (!editedByOfferType.has(offerType)) {
            editedByOfferType.set(offerType, editedIdsFor(state, offerType));
        }
        return editedByOfferType.get(offerType) as Set<number>;
    };
    return Object.fromEntries(
        Object.entries(paymentData).map(([termId, record]) => [
            termId,
            record?.appliedIncentiveIds?.length ? withoutIds(record, editedFor(termId.split('_')[0] as OfferType)) : record
        ])
    );
};

const buildOfferTermPayload = (payload: any, state: StateTree) => {
    const offer = state.offer || ({} as Offer);

    const financeInputs = offerReduxSelectors.getOfferDetails(state, FINANCE) || {};
    const leaseInputs = offerReduxSelectors.getOfferDetails(state, LEASE) || {};
    const cashInputs = offerReduxSelectors.getOfferDetails(state, CASH) || {};
    const balloonInputs = offerReduxSelectors.getOfferDetails(state, BALLOON) || {};

    const isUPQ = offerReduxSelectors.getIsUserProgramQuotes(state);
    const dealSelectionOf = (offerType: typeof FINANCE | typeof LEASE, selectedIncentives: string[] | undefined) =>
        offerReduxUtils.incentives.isGridIncentiveManagementEnabled(state, offerType) && selectedIncentives
            ? { selectedIncentives }
            : {};

    const penciledInputs = {
        finance: {
            downPayment: financeInputs.downPayment,
            offerPrice: financeInputs.offerPrice,
            overridesByTerm: financeInputs.overridesByTerm,
            retailPriceOverride: financeInputs.retailPriceOverride,
            term: financeInputs.term,
            creditDecisionEnabled: financeInputs.creditDecisionEnabled,
            // TODO Delete creditDecisionLenderName after feature toggle enableAppliedLenderInfo has been deleted
            creditDecisionLenderName: financeInputs.creditDecisionLenderName,
            creditDecisionLender: financeInputs.creditDecisionLender,
            creditDecisionStatus: financeInputs.creditDecisionStatus,
            incentiveTotalOverride: financeInputs.incentiveTotalOverride,
            manualIncentives: financeInputs.manualIncentives,
            manualTotalTax: financeInputs.manualTotalTax,
            titlingLender: financeInputs.titlingLender,
            ...dealSelectionOf(FINANCE, financeInputs.selectedIncentives)
        },
        lease: {
            acqFeeOverride: leaseInputs.acqFeeOverride,
            acqFeeUpFrontOverride: leaseInputs.acqFeeUpFrontOverride,
            annualMiles: leaseInputs.annualMiles,
            downPayment: leaseInputs.downPayment,
            offerPrice: leaseInputs.offerPrice,
            overridesByTerm: leaseInputs.overridesByTerm,
            retailPriceOverride: leaseInputs.retailPriceOverride,
            securityDepositOverride: leaseInputs.securityDepositOverride,
            term: leaseInputs.term,
            creditDecisionEnabled: leaseInputs.creditDecisionEnabled,
            // TODO Delete creditDecisionLenderName after feature toggle enableAppliedLenderInfo has been deleted
            creditDecisionLenderName: leaseInputs.creditDecisionLenderName,
            creditDecisionLender: leaseInputs.creditDecisionLender,
            creditDecisionStatus: leaseInputs.creditDecisionStatus,
            incentiveTotalOverride: leaseInputs.incentiveTotalOverride,
            mileageChargeOverride: leaseInputs.mileageChargeOverride,
            manualIncentives: leaseInputs.manualIncentives,
            manualTotalTax: leaseInputs.manualTotalTax,
            capitalizeFirstPayment: leaseInputs.capitalizeFirstPayment,
            ...(isUPQ && {
                adjustedResidualAmountOverride: leaseInputs.adjustedResidualAmountOverride,
                adjustedResidualPercentOverride: leaseInputs.adjustedResidualPercentOverride,
                adjustedResidualTypeOverride: leaseInputs.adjustedResidualTypeOverride,
                sellRateOverride: leaseInputs.sellRateOverride,
                buyRateOverride: leaseInputs.buyRateOverride
            }),
            titlingLender: leaseInputs.titlingLender,
            ...dealSelectionOf(LEASE, leaseInputs.selectedIncentives)
        },
        cash: {
            appliedIncentiveAmount: offerReduxSelectors.getAllIncentiveTotalByOfferType(state, CASH),
            appliedIncentiveIds: cashInputs.appliedIncentiveIds,
            appliedIncentives: cashInputs.appliedIncentives,
            dealerFees: cashInputs.dealerFees,
            dealerFeesTotal: cashInputs.dealerFeesTotal,
            dmvFees: cashInputs.dmvFees,
            incentives: cashInputs.incentives,
            initialOfferPrice: cashInputs.initialOfferPrice,
            offerPrice: cashInputs.offerPrice,
            retailPriceOverride: cashInputs.retailPriceOverride,
            selectedIncentives: cashInputs.selectedIncentives,
            taxBreakdown: cashInputs.taxBreakdown,
            totalTax: cashInputs.totalTax,
            manualIncentives: cashInputs.manualIncentives,
            manualTotalTax: cashInputs.manualTotalTax
        },
        balloon: {
            downPayment: balloonInputs.downPayment,
            offerPrice: balloonInputs.offerPrice,
            overridesByTerm: balloonInputs.overridesByTerm,
            retailPriceOverride: balloonInputs.retailPriceOverride,
            term: balloonInputs.term,
            creditDecisionEnabled: balloonInputs.creditDecisionEnabled,
            creditDecisionLenderName: balloonInputs.creditDecisionLenderName,
            creditDecisionLender: balloonInputs.creditDecisionLender,
            creditDecisionStatus: balloonInputs.creditDecisionStatus,
            incentiveTotalOverride: balloonInputs.incentiveTotalOverride,
            manualIncentives: balloonInputs.manualIncentives,
            manualTotalTax: balloonInputs.manualTotalTax,
            balloonPaymentAmountOverride: balloonInputs.balloonPaymentAmountOverride,
            balloonTypeOverride: balloonInputs.balloonTypeOverride,
            titlingLender: balloonInputs.titlingLender
        }
    };

    const isOverrideIndicatorEnabled = featureToggleSelectors.isPgOverrideIndicatorEnabled(state);
    for (const offerType of [FINANCE, LEASE, CASH, BALLOON] as const) {
        const paymentGridData = getPaymentGridSaveData(state, offerType, { isOverrideIndicatorEnabled });
        if (paymentGridData) (penciledInputs[offerType] as IPencilOfferInputs).paymentGridData = paymentGridData as any;
    }

    payload.terms = {
        paymentData: withoutEditedAppliedIdsByTerm(offerReduxSelectors.getTermsByIdForOffer(offer), state),
        penciledInputs
    };

    if (offer) {
        payload.selectedFinanceTerm = offerReduxSelectors.getTermIdForOfferType(offer, FINANCE);
        payload.selectedLeaseTerm = offerReduxSelectors.getTermIdForOfferType(offer, LEASE);
        payload.selectedBalloonTerm = offerReduxSelectors.getTermIdForOfferType(offer, BALLOON);
    }
    return payload;
};

/**
 * Create the payload necessary to hit the Offer Service
 * @param state - Current Application State
 */
export const createGatewayOfferPayload = (
    state: StateTree,
    offerUpdateType?: string,
    _purpose = BuildPayloadPurpose.Unknown,
    selectedOfferType?: OfferType
) => {
    const applicationName = 'CMD';

    const offerType = selectedOfferType ?? offerReduxSelectors.getCurrentOfferType(state);
    const isCash = offerType === CASH;

    const isEnabledFniFailure: boolean | undefined = undefined; // enableFniFailure();
    const isEnabledFniFailFeatureToggle: boolean | undefined = undefined; // enableFniFailFeatureToggle();
    const commonOrgId = offerReduxSelectors.getCommonOrgId(state);
    const commonConsumerId = offerReduxSelectors.getShopperCommonConsumerId(state);
    const currentCalculationEndpoint = offerReduxSelectors.getCalculationEndpointByOfferType(
        state,
        offerReduxSelectors.getCurrentOfferType(state)
    );
    const isUserProgramQuotes = offerReduxSelectors.getIsUserProgramQuotes(state);
    const dealerId = dealerSelectors.getDealerId(state);
    const offerSummary = offerReduxSelectors.getOfferTerm(state, selectedOfferType);
    const transferRegistrationDetails = offerReduxSelectors.getTransferRegistrationDetails(state);
    const { downPayment, offerPrice: currentOfferPrice, initialOfferPrice } = offerReduxSelectors.getOfferDetails(state, offerType);
    const dealSummary = (offerSummary as any).dealSummary || [];
    const creditAppReferenceId = creditAppSelectors.getCreditAppReferenceId(state);
    const accelerateCreditAppId = creditAppSelectors.getAccelerateCreditAppId(state);
    const coApplicantEmail = offerReduxSelectors.getCreditAppCoApplicantEmail(state);
    const selectedShopper = offerReduxSelectors.getShopperInfo(state);
    const email = selectedShopper.email ? selectedShopper.email.trim() : '';
    const enableCorrectPhoneHandling = featureToggleSelectors.isCorrectPhoneHandlingEnabled(state);
    const shouldConvertPhoneNumbers = enableCorrectPhoneHandling || featureToggleSelectors.isConvertPhoneNumberEnabled(state);
    const shopper = {
        ...selectedShopper,
        email,
        firstName: selectedShopper.firstName ? selectedShopper.firstName.trim() : '',
        lastName: selectedShopper.lastName ? selectedShopper.lastName.trim() : '',
        zip: selectedShopper.zip || dealerSelectors.getDealerZip(state),
        ...(shouldConvertPhoneNumbers && convertPhoneNumbers(selectedShopper, enableCorrectPhoneHandling))
    };
    const selectedCoBuyer = offerReduxSelectors.getCoBuyerInfo(state);
    const coBuyer = selectedCoBuyer
        ? {
              ...selectedCoBuyer,
              ...(shouldConvertPhoneNumbers && convertPhoneNumbers(selectedCoBuyer, enableCorrectPhoneHandling))
          }
        : null;
    const route = widgetSettingsSelectors.getSourceRoute(state);
    const vdpUrl = vdpSourceSelectors.getVdpUrl(state);
    const sponsor = vdpSourceSelectors.getVdpSponsor(state);
    const tradeIn = tradeInSelectors.getTradeIn(state);
    const tradeInStatus = tradeInSelectors.getTradeInStatus(state);
    const connectionId = connectionSelectors.getConnectionId(state);
    const hasBeenSent = offerInfoSelectors.getHasBeenSent(state);
    const selectedVehicle = offerReduxSelectors.getVehicleDetails(state);
    const deviceType = offerReduxSelectors.getDeviceType(state);
    const offerId = offerInfoSelectors.getOfferId(state);
    const sessionId = offerReduxSelectors.getSessionId(state) as string;

    // When enableMVOptimizedSaveOffer is enabled, include vehicle fields to skip backend vehicle service call
    const isOptimizedSaveEnabled = featureToggleSelectors.isEnableMVOptimizedSaveOffer(state);
    const fullVehicleData = vehicleSelectors.getVehicle(state);

    // Pick up MPG that was merged onto state.vehicle from the fuel-economy API so it flows into saveOffer
    // and gets persisted on the deal. On subsequent loads, init returns these and the slideout skips the API.
    const fuelEconomyFields = {
        ...(fullVehicleData?.fuelEconomy && { fuelEconomy: fullVehicleData.fuelEconomy }),
        ...(fullVehicleData?.cityFuelEconomy && { cityFuelEconomy: fullVehicleData.cityFuelEconomy }),
        ...(fullVehicleData?.highwayFuelEconomy && { highwayFuelEconomy: fullVehicleData.highwayFuelEconomy })
    };

    // Echoed back so the full-PUT save preserves them; omitting a cleared field is how the PUT deletes it.
    const vehicleUse = fullVehicleData && vehicleSelectors.getVehicleUse(state);
    const { principalDriverName } = fullVehicleData ?? {};

    // An empty value on state.vehicle is a clear, so send undefined and let the PUT delete it. An absent
    // key never hydrated, so leave whatever selectedVehicle spread in.
    const overrideIfInVehicleState = (key: string, value?: string) =>
        fullVehicleData && key in fullVehicleData ? { [key]: value || undefined } : {};

    const vehicle = {
        ...selectedVehicle,
        ...fuelEconomyFields,
        condition: selectedVehicle?.condition?.toLowerCase(),
        odometerMileageType: (selectedVehicle as IVehicle)?.odometerMileageType || OdometerMileageTypes.Estimate,
        ...(selectedVehicle?.notes && { notes: stripHtmlTags(selectedVehicle.notes) }),
        ...overrideIfInVehicleState('vehicleUse', vehicleUse),
        ...overrideIfInVehicleState('principalDriverName', principalDriverName),
        ...(isOptimizedSaveEnabled &&
            fullVehicleData && {
                engine: fullVehicleData.engine,
                transmission: fullVehicleData.transmission,
                bodyStyle: fullVehicleData.bodyStyle
            })
    } as IVehicle;
    const canNegotiateNew = !dealerSelectors.getIsNewVehicleSinglePrice(state);
    const canNegotiateUsed = !dealerSelectors.getIsUsedVehicleSinglePrice(state);
    const mergedDeal = offerInfoSelectors.getMergedDeal(state);
    const isSinglePrice = vehicleUtils.isSinglePrice(canNegotiateNew, canNegotiateUsed, vehicle.condition);
    const listingCode = vdpSourceSelectors.getListingCode(state, sponsor);
    const metaData = metaDataSelectors.getMetaData(state);
    const totalIncentives =
        offerType === CASH
            ? offerReduxSelectors.findAllIncentiveTotalFromOffer(state.offer, offerType)
            : offerSummary.appliedIncentiveAmount;
    const messageToDealer = offerReduxSelectors.getMessage(state);
    const dealRefId = offerSelectors.getDealRefId(state); // TODO: clean up dealRefId - remove fsDealRefId and deal_ref_id
    const fsDealRefId = dealRefId;
    const deal_ref_id = dealRefId; // Temp value, during finance services migration
    const hasShopperEngagedWithOffer = createDealSelectors.hasShopperEngagedWithOffer(state);
    const dealExchangeDealId = selectedOfferSelectors.getSelectedOfferDealXgId(state);
    const dealExchangeVersionId = selectedOfferSelectors.getSelectedOfferDealXgVersion(state);

    const offerSource = offerReduxSelectors.getOfferSource(state) || compositeSelectors.getPersona(state);
    const shortDisclaimerData = offerReduxSelectors.getShortDisclaimerData(state);
    const paymentDisclaimer = formatUtils.formatShortDisclaimer(shortDisclaimerData);
    const cmdMessageToShopper = offerReduxSelectors.getCmdMessageToShopper(state) as string;
    const cmdMessageToSalesperson = offerReduxSelectors.getCmdMessageToSalesperson(state);
    const creditAppId = offerReduxSelectors.getCreditAppId(state) as string;
    const globalCustomerId = offerReduxSelectors.getGlobalCustomerId(state);
    const isIncentivesCompleted = Boolean(offerReduxSelectors.getSelectedConditionalIncentivesToDisplay(state)?.length);
    const isTermsCompleted = true; // Terms activity is marked completed when offer is submitted
    const cashApplyMethod = offerReduxSelectors.getCashOption(state);
    const leaseOfferPrice = offerReduxSelectors.getLeaseOfferPrice(state, LEASE);
    const offerDetails = offerReduxSelectors.getOfferDetails(state);

    const offerPrice = offerType === LEASE ? leaseOfferPrice : currentOfferPrice;
    const specialPaymentFormula = offerDetails?.specialPaymentFormula;
    const isLockSupported = dealLockingSelectors.isLockSupported(state);
    const isLocked = dealLockingSelectors.getLockPaymentInputsInSVByDealType(state);

    // Soft pull consent
    const enableSoftPullConsentMV = featureToggleSelectors.isEnableSoftPullConsentMV(state);
    const softPullConsentProvided = offerInfoSelectors.getSoftPullConsentProvided(state);
    const deferredDownPayment = offerReduxSelectors.getDeferredDownPayment(state);

    const priorLeaseBalance =
        featureToggleSelectors.isPriorBalanceEnabled(state) && offerReduxSelectors.hasPriorLeaseBalance(state)
            ? offerReduxSelectors.getPriorLeaseBalance(state)
            : undefined;

    // https://ghe.coxautoinc.com/DigitalRetailing/dr-dash/issues/801
    let payload: SaveOfferPayload = {
        offerId,
        ...(offerType === LEASE && cashApplyMethod !== null && { cashApplyMethod }),
        ...(coBuyer != null && { coBuyer }), // include coBuyer if it has a value
        commonConsumerId,
        commonOrgId,
        currentCalculationEndpoint,
        dealerId,
        dealExchangeDealId,
        dealExchangeVersionId,
        enableAppliedLenderInfo: featureToggleSelectors.getEnableAppliedLenderInfo(state),
        ...(isLockSupported && {
            dealXgDetails: {
                isLocked
            }
        }),
        isEnabledFniFailure,
        isEnabledFniFailFeatureToggle,
        isUserProgramQuotes,
        dealRefId, // TODO: clean up dealRefId
        deal_ref_id,
        fsDealRefId,
        type: offerType,
        offerPrice,
        initialOfferPrice,
        totalFee: offerSummary.totalFee,
        totalTax: offerSummary.totalTax,
        downPayment: isCash ? offerReduxSelectors.getCashTotalAmountDueAfterCredits(state) : downPayment ?? 0,
        totalFinanced: isCash ? undefined : (offerSummary as PaymentTerm).amountFinanced,
        monthlyPayment: isCash ? undefined : (offerSummary as PaymentTerm).monthlyPayment,
        monthlyPaymentRounded: isCash ? undefined : (offerSummary as PaymentTerm).monthlyPaymentRounded,
        dealSummary,
        creditApp: {
            creditAppReferenceId,
            accelerateCreditAppId,
            coApplicantEmail
        },
        creditAppId,
        creditScore: offerReduxUtils.buildCreditScore(state),
        incentives: {
            ...state.offer?.incentives,
            total: totalIncentives || 0
        },
        shopper,
        source: {
            listingCode,
            route,
            vdpUrl,
            sponsor
        },
        vehicle: {
            ...vehicle,
            wholesaleValue: vehicle?.wholesaleValue ? vehicle.wholesaleValue : undefined,
            wholesaleValueType: vehicle?.wholesaleValueType ? vehicle.wholesaleValueType : undefined
        },
        mergedDeal,
        isSinglePrice,
        tradeIn: tradeInStatus.isCompleted ? tradeIn : {},
        isTradeCompleted: tradeInStatus.isCompleted,
        isIncentivesCompleted,
        isTermsCompleted,
        isTradeSkipped: tradeInStatus.isSkipped,
        bypassDealSummary: true,
        connectionId,
        payment: {
            ...withoutEditedAppliedIds(offerSummary, state, offerType),
            ...(offerSummary.discount != null ? { discountOfMsrp: offerSummary.discount } : {}),
            taxItems: offerReduxSelectors.getTaxItemsWithOverrides(state, offerType),
            ...transferRegistrationDetails
        },
        messageToDealer,
        offerUpdateType: offerUpdateType || undefined,
        hasBeenSent,
        deviceType,
        metaData,
        sessionId,
        isTestLead: leadFormInfoSelectors.isTestLead(state),
        hasShopperEngagedWithOffer,
        offerSource,
        paymentDisclaimer,
        paymentTermsFallback: offerSelectors.getPaymentTermsFallback(state),
        cmdMessageToShopper,
        cmdMessageToSalesperson,
        globalCustomerId,
        transferToDMS: pushToDmsSelectors.getTransferToDMS(state),
        applicationName,
        latestPushToDmsPlus: dealXgDetailsSelectors.getLatestPushToDmsPlus(state),
        ...(enableSoftPullConsentMV && {
            softPullConsentProvided
        }),
        ...(priorLeaseBalance && { priceDetails: { priorLeaseBalance } })
    };

    payload = buildOfferTermPayload(payload, state);

    const retainedProgram = programsSelectors.getRetainedProgramDisplay(state);
    if (!isCash && retainedProgram) {
        const selectedTerm = offerReduxSelectors.getSelectedTerm(state);
        const retained = featureToggleSelectors.isRetainedProgramLatchEnabled(state) ? retainedProgram.program : undefined;
        const lenderProgram = retained
            ? { ...selectedTerm?.lenderProgram, ...retained.lenderProgram }
            : {
                  ...selectedTerm?.lenderProgram,
                  programName: retainedProgram.programName || selectedTerm.lenderProgram?.programName
              };
        const lenderLegalName =
            retained?.lenderLegalName || retainedProgram.lenderLegalName || selectedTerm.lenderProgram?.lenderLegalName;
        payload.payment = {
            ...payload.payment,
            lenderLegalName,
            lenderProgram,
            ...(retained && {
                ...(retained.lenderCode && { lenderCode: retained.lenderCode }),
                ...(retained.lenderName && { lenderName: retained.lenderName }),
                ...(retained.branchName && { branchName: retained.branchName }),
                ...(retained.programLenderName && { programLenderName: retained.programLenderName }),
                ...(retained.creditTier != null && { creditTier: retained.creditTier }),
                ...(retained.creditTierDescription && { creditTierDescription: retained.creditTierDescription }),
                ...(retained.lenderAddress && { lenderAddress: retained.lenderAddress }),
                ...(retained.lenderPhoneNumber && { lenderPhoneNumber: retained.lenderPhoneNumber })
            })
        };
    }

    const lead = leadSelectors.getLead(state);
    if (!isLeadEmpty(lead)) {
        payload = { ...payload, ...{ lead } };
    }

    if (offerType === FINANCE) {
        const { months, apr } = offerSummary as PaymentTerm;
        payload = {
            ...payload,
            ...{
                financeTerms: { term: months, apr } as any // TODO: Let John Richardson know to fix this on 2024-02-01
            }
        };
        const hasSpecialPaymentFormula =
            specialPaymentFormula && 'firstTermPaymentDetail' in offerSummary && 'secondTermPaymentDetail' in offerSummary;

        if (hasSpecialPaymentFormula) {
            const { firstTermPaymentDetail, secondTermPaymentDetail } = offerSummary;

            payload.payment = {
                ...payload.payment,
                specialPaymentFormula,
                firstTermPaymentDetail: formatPaymentDetail({ detail: firstTermPaymentDetail }),
                secondTermPaymentDetail: formatPaymentDetail({ detail: secondTermPaymentDetail })
            };
        }
        if (deferredDownPayment) {
            payload.payment = {
                ...payload.payment,
                deferredDownPayment
            };
        }
    } else if (offerType === BALLOON) {
        const { months, apr } = offerSummary as PaymentTerm;
        const balloonExpiration = offerReduxSelectors.getBalloonExpiration(state);
        const balloonInputs = offerReduxSelectors.getOfferDetails(state, BALLOON) || {};

        // Use override values if present, otherwise fall back to term values
        const totalBalloonPaymentAmount = balloonInputs.balloonPaymentAmountOverride ?? offerReduxSelectors.getBalloonAmount(state);
        const balloonType = balloonInputs.balloonTypeOverride ?? offerReduxSelectors.getBalloonType(state);

        payload = {
            ...payload,
            ...{
                balloonTerms: { term: months, apr } as FinancingTerms // re-used finance term type since it is identical with balloon
            }
        };
        payload.payment = {
            ...payload.payment,
            balloonExpiration,
            totalBalloonPaymentAmount,
            balloonType
        };
    } else if (offerType === LEASE) {
        const { months } = offerSummary as PaymentTerm;
        const mileage = offerReduxSelectors.getAnnualMiles(state) || offerReduxSelectors.getInputAnnualMiles(state) || 1;
        const retroMilesThreshold = offerReduxSelectors.getRetroMiles(state);
        const standardMiles = offerReduxSelectors.getStandardAllowedMiles(state);
        const initialMilesThreshold = offerReduxSelectors.getThresholdMiles(state);
        const initialMileageCharge = offerReduxSelectors.getInceptionRate(state);
        const extraCostPerMile = offerReduxSelectors.getExcessMileRate(state);
        const extraMiles = offerReduxSelectors.getTotalExcessMilesPurchased(state);
        const totalMiles = offerReduxSelectors.getTotalMiles(state);
        const secondPaymentDueDate = offerReduxSelectors.getSecondPaymentDueDate(state);

        const excessMileageCharge =
            offerReduxSelectors.getMileageChargeOverride(state) || offerReduxSelectors.getExcessMileageCharge(state);

        const vehicleDetails = offerReduxSelectors.getVehicleDetails(state);
        const paymentFrequency = paymentFrequencySelectors.getCalculatedPaymentFrequency(state);
        const odometerMileage = vehicleDetails?.mileage || 0;
        const isOnePayLease = paymentFrequency === PaymentScheduleTypes.OnePayLease;

        payload.payment = {
            ...payload.payment,
            retroMilesThreshold,
            standardMiles,
            initialMilesThreshold,
            initialMileageCharge,
            extraCostPerMile,
            extraMiles,
            totalMiles,
            excessMileageCharge,
            odometerMileage,
            secondPaymentDueDate
        };

        // Remove secondPaymentDueDate from payload.payment if it exists and is OnePayLease
        // OnePayLease is a single payment lease, so it should not have a second payment date
        if (isOnePayLease) {
            payload.payment = getPaymentWithoutSecondPaymentDueDate(payload.payment);
        }

        payload = {
            ...payload,
            ...{
                leaseTerms: { term: months, mileage }
            },
            deductDueAtSigning: dealerSelectors.getIsDueAtSigninAmountDeduct(state),
            capCostDetails: {
                adjustedCapCost: offerReduxSelectors.getAdjustedCapCost(state),
                capReductionCash: offerReduxSelectors.getCapCostReduction(state, offerType),
                grossCapCost: offerReduxSelectors.getCapCostTotal(state)
            }
        } as any; // TODO: Let John Richardson know to fix this on 2024-02-01
    } else {
        // offerType is cash
        const { dealerFees, dmvFees } = offerReduxSelectors.getOfferDetails(state, offerType);
        payload.fees = dealerFees || [];
        payload.dmvFees = dmvFees || 0;
    }

    delete (payload.payment as IPencilOfferInputs).psRawData;

    return payload;
};

export const convertPhoneNumbers = (customer: IShopper | CoBuyer, enableCorrectPhoneHandling?: boolean) => {
    // Skip if there is already a cellphone
    if (!customer || customer.cellPhone) {
        return {};
    }

    const { homePhone, workPhone, phone } = customer;

    if (enableCorrectPhoneHandling) {
        const cellPhone = resolveCellPhone({ homePhone, workPhone, phone });
        return cellPhone ? { cellPhone } : {};
    }

    // If only phone exists, use it as cellPhone
    if (!homePhone && !workPhone && phone) {
        return { cellPhone: phone };
    }

    // If homePhone exists, move it to cellPhone
    if (homePhone) {
        return {
            cellPhone: homePhone,
            homePhone: ''
        };
    }

    // If workPhone exists, move it to cellPhone
    if (workPhone) {
        return {
            cellPhone: workPhone,
            workPhone: ''
        };
    }

    return {};
};

const formatListingOverride = (state: StateTree) => {
    const listingOverride = dealerSelectors.getListingOverride(state);
    if (!listingOverride) {
        return undefined;
    }

    const reviewDealUrlEnabled = listingOverride?.reviewDealUrlEnabled || false;

    return {
        reviewDealUrlEnabled
    };
};

export const createDealerNode = (state: StateTree) => {
    const dealer = dealerSelectors.getDealerState(state);
    return {
        dealerId: dealer.dealerId,
        dnaAccountId: dealer.dnaAccountId,
        useFnI2: dealer.useFnI2 || false,
        isCrmIntegrationEnabled: dealer.isCrmIntegrationEnabled,
        // always true since the isFordBlueAdvantageEnabled toggle was retired (US2020830)
        isFordBlueAdvantageEnabled: true,
        isTCPAEnabled: dealer.isTCPAEnabled || false,
        isTCPACheckboxDisabled: dealer.isTCPACheckboxDisabled || false,
        leadRoutingOverrideSettings: dealer.leadRoutingOverrideSettings,
        reviewDealUrlEnabled: dealer.reviewDealUrlEnabled || false,
        listingOverride: formatListingOverride(state),
        theme: state.dealer.theme,
        dealerTaxAddress: dealer.dealerTaxAddress,
        enableNonAMDCustomerExperience: dealer.enableNonAMDCustomerExperience || false
    };
};

export const createMvDealStatePayload = (state: StateTree) => {
    const isDealCentralWithFnI = dealerSelectors.isDealCentralWithFnI(state);
    const managerViewPayload: MvDealStatePayload = {};

    if (isDealCentralWithFnI) {
        managerViewPayload.dealMode = dealModeSelectors.isFnIDealMode(state) ? DealMode.fiMode : DealMode.deskingMode;
    }
    managerViewPayload.breakdownState = baseDealSelectors.getBreakdownStatus(state) ? 'open' : 'close';

    return Object.keys(managerViewPayload).length ? managerViewPayload : null;
};

export const createAutoSelectStatePayload = (state: StateTree): AutoSelectStatePayload | null => {
    // null leaves the save payload untouched while the feature is toggled off. Explicit Lender P1 also
    // records overridden fields — the acquisition fee's indicator is restored from this on load — so it
    // has to emit the payload even where Improved Auto Select is off.
    const isImprovedAutoSelectEnabled = featureToggleSelectors.isImprovedAutoSelectEnabled(state);
    if (
        !isImprovedAutoSelectEnabled &&
        !featureToggleSelectors.isExplicitLenderP1Enabled(state) &&
        !featureToggleSelectors.isAspPaymentGridRedesignEnabled(state)
    ) {
        return null;
    }

    return {
        // Only Improved Auto Select owns a selection to record. Explicit Lender P1 hides the checkbox, so
        // `getIsAutoSelectChecked` is never written and this would fall through to the endpoint, persisting a
        // boolean no dealer chose; enabling Improved Auto Select later would replay it as a restored
        // selection. `checked` is optional and the load path gates on `typeof checked === 'boolean'`, so
        // omitting it seeds from the endpoint exactly as a deal with nothing stored does.
        ...(isImprovedAutoSelectEnabled && {
            // Baggage holds one boolean, so it carries the deal type being saved.
            checked:
                manualFieldOverridesSelectors.getIsAutoSelectChecked(state, offerReduxSelectors.getCurrentOfferType(state)) ??
                offerReduxSelectors.getIsProgramQuotes(state)
        }),
        overriddenFields: manualFieldOverridesSelectors.getManualFieldOverrides(state)
    };
};

export const createLeaseResidualOverridePayload = (state: StateTree, offerType: OfferType): LeaseResidualOverridePayload | null => {
    if (offerType !== LEASE || !featureToggleSelectors.isAspPaymentGridRedesignEnabled(state)) return null;

    return paymentPayloadUtils.getPqLeaseResidualOverride(state) ?? null;
};

export type Context = {
    listingCode?: string;
    shopperPageUrl: string;
    testDriveDeepLinkUrl?: string;
    vdpUrl?: string;
    sponsor?: string;
};

export type GatewayPayload = InitPayloadOffer & {
    dealer?: Dealer;
    tradeInVehicle: TradeInState;
    widgetSettings: { sponsor: string };
    financing: { term: number; annualMiles: any; apr: any };
    creditAppReferenceId: string;
    context?: Context;
    offer: GatewayOfferPayload;
};

export type GatewayOfferPayload = Offer & {
    type?: string;
    price?: number;
    initialPrice?: number;
    downPayment?: number;
    monthlyPaymentRounded?: number;
    messageToDealer?: string | undefined;
    offerId?: string;
    dealRefId?: string; // TODO: clean up dealRefId
    fsDealRefId?: string;
    amountFinanced?: number;
    dueAtSigning?: number;
    dealSummary?: DealSummaryItem[];
    cmdMessageToShopper?: string;
};

/**
 * Create the payload to hit the BFF Test Drive Endpoint
 * @param state - Current Application State
 */
export const createOfferPayload = (_state: StateTree, _offerUpdateType?: string): GatewayPayload => {
    // NOTE: This is most likely not needed, but we need to pass it to trade anyway so I'm keeping it hanging around until we've
    //   validated that it won't need to be implemented.
    return null as unknown as GatewayPayload;
};

type ExtendedHostSelectors = paymentTypes.HostSelectors & {
    getIncludedTradeInItems?: (state: StateTree) => any;
};

export const buildPaymentPayloadHostSelectors = (overrideSelectors?: paymentTypes.HostSelectors): paymentTypes.HostSelectors => {
    const paymentMiddlewareConfigSelectorsDefault: ExtendedHostSelectors = {
        shouldFakePaymentFailure: (_state: StateTree) => false,
        shouldKeepPaymentsOnFailure: () => true,
        isManagerView: true,
        getPartnerCode,
        getPartnerDealerId,
        getPaymentProducts: compositeSelectors.getPaymentProducts,
        getTradeIn,
        getIncludedTradeInItems,
        shouldUseMultiTradeIns: isTradeInsArray,
        getIsDueAtSigninAmountDeduct: dealerSelectors.getIsDueAtSigninAmountDeduct,
        getExplicitLenderLock: programsSelectors.getExplicitDealProgram
    };

    return { ...paymentMiddlewareConfigSelectorsDefault, ...overrideSelectors };
};

type PaymentTermDetail = FlexBuyPayment['firstTermPaymentDetail'] | FlexBuyPayment['secondTermPaymentDetail'];

export const formatPaymentDetail = ({ detail }: { detail: PaymentTermDetail | undefined }) => {
    if (!detail) {
        return undefined;
    }

    if ('firstPaymentDueDate' in detail && detail.firstPaymentDueDate) {
        return {
            ...detail,
            firstPaymentDueDate: new Date(detail.firstPaymentDueDate).toJSON()
        };
    } else if ('secondPaymentDueDate' in detail && detail.secondPaymentDueDate) {
        return {
            ...detail,
            secondPaymentDueDate: new Date(detail.secondPaymentDueDate).toJSON()
        };
    }

    // returning the detail date field as missing or null/undefined
    return detail;
};

// In the near future, this state type will be solidified
export const buildMultipleOfferSavePayload = (
    state: any,
    purpose: BuildPayloadPurpose = BuildPayloadPurpose.Unknown,
    selectedOfferType?: OfferType,
    selectedScopeIds?: string[]
): ISaveOffer => {
    const primaryScopeId = scopedStateSelectors.getPrimaryScopeId(state);
    const primaryScopeState = scopedStateSelectors.getScopeStateById(state, primaryScopeId);
    const primaryOfferPayload = buildOfferSavePayload(primaryScopeState as StateTree, purpose, selectedOfferType, false);
    const allOrderedScopeIds = scopedStateSelectors.getInitializedScopesOrder(state);
    const orderedScopeIds = selectedScopeIds?.length
        ? allOrderedScopeIds.filter((id: string) => selectedScopeIds.includes(id))
        : allOrderedScopeIds;

    // Always copy fuel economy from primary vehicle state if it exists
    const primaryVehicle = vehicleSelectors.getVehicle(primaryScopeState as StateTree);
    const primaryFuelEconomy = primaryVehicle?.fuelEconomy ?? primaryVehicle?.combinedFuelEconomy;
    if (primaryFuelEconomy && primaryOfferPayload.offer?.vehicle) {
        primaryOfferPayload.offer.vehicle.fuelEconomy = primaryFuelEconomy;
    }

    const alternateScenarios: ISaveOffer[] = [];
    for (const scopeId of orderedScopeIds) {
        if (scopeId !== primaryScopeId) {
            const scopeState = scopedStateSelectors.getScopeStateById(state, scopeId);
            const alternateScenario = buildOfferSavePayload(scopeState as StateTree, purpose, selectedOfferType, true);

            // Always copy fuel economy from scenario vehicle state if it exists
            const scopeVehicle = vehicleSelectors.getVehicle(scopeState as StateTree);
            const scenarioFuelEconomy = scopeVehicle?.fuelEconomy ?? scopeVehicle?.combinedFuelEconomy;
            if (scenarioFuelEconomy && alternateScenario.offer?.vehicle) {
                alternateScenario.offer.vehicle.fuelEconomy = scenarioFuelEconomy;
            }

            alternateScenario.meta = {
                scenarioId: scopeId,
                activeDealType: offerReduxSelectors.getCurrentOfferType(scopeState as StateTree)
            };
            alternateScenarios.push(alternateScenario);
        }
    }
    if (alternateScenarios.length) {
        primaryOfferPayload.comparisonInfo = {
            order: orderedScopeIds,
            alternateScenarios
        } as any;
        primaryOfferPayload.meta = {
            scenarioId: primaryScopeId
        };
    }

    primaryOfferPayload.baggage = addMvAutoSelectProgramSavedBaggage(primaryOfferPayload.baggage);

    return primaryOfferPayload;
};

export const buildOfferSavePayload = (
    state: StateTree,
    purpose = BuildPayloadPurpose.Unknown,
    selectedOfferType?: OfferType,
    isAlternateScenario = false
): ISaveOffer => {
    const offerType = selectedOfferType ?? offerReduxSelectors.getCurrentOfferType(state);
    const connectionId = connectionSelectors.getConnectionId(state);
    const vin = vehicleSelectors.getVehicleVin(state);
    const username = bridgeUserSelectors.getBridgeUsername(state);
    const userFullName = bridgeUserSelectors.getFullName(state);
    const employees = buildEmployeeDetails(state);
    const incentives = buildIncentivesNode(state, selectedOfferType);
    const accessories = toOfferTypeSpecificFormat(
        AccessoriesAdapter.toTargetFormat(accessoriesSelectors.getAccessories(state)),
        offerType
    );
    const menuPayload: SaveOfferMenuProductsPayload = createMenuPayload(state);
    const offerPayload: SaveOfferPayload = createGatewayOfferPayload(state, undefined, purpose, selectedOfferType);
    const tradesPayload: ITradeIn[] = createTradesPayload(state);
    const dealerPayload: SaveOfferDealer = createDealerNode(state);
    const calculationDetails = createCalculationDetailsForOfferSave(state);
    const vehicleInsurance = baseDealSelectors.getVehicleInsuranceInfo(state);
    const appliedTemplateId = dealTemplateSelectors.getAppliedDealTemplateId(state);
    const dealLifecycle: any = dealLifecycleSelectors.getDealLifecycle(state);
    const mvDealStatePayload = createMvDealStatePayload(state);
    const autoSelectStatePayload = createAutoSelectStatePayload(state);
    const leaseResidualOverridePayload = isAlternateScenario ? undefined : createLeaseResidualOverridePayload(state, offerType);
    const darwinBaselinePayload = getVppDarwinBaselinePayload(state);
    const isMVVppOverrideIndicatorEnabled = featureToggleSelectors.isMVVppOverrideIndicatorEnabled(state);

    const shouldDeleteTestDrive = state.testDrive === null;
    const shouldDeleteVehicleFulfillment = offerReduxSelectors.getVehicleFulfillmentRaw(state) === null;
    const shouldReleaseReservation = state.reservation === null;

    const isSoftPullIntegrationEnabled = featureToggleSelectors.useSoftPullIntegrationMV(state);
    const isSoftPullDealerEnabled = featureToggleSelectors.isEnableSoftPullConsentMV(state);
    const qualificationIds = deskingSelectors.getQualificationIds(state);
    const includeQualificationIds = isSoftPullIntegrationEnabled && isSoftPullDealerEnabled && qualificationIds.length > 0;

    const baggageFuelEconomy = baggageFuelEconomySelectors.getBaggageFuelEconomy(state);
    const rightToCancelDetails = baseDealSelectors.getRightToCancelDetails(state);
    const hasRightToCancelData =
        rightToCancelDetails.rightToCancelDays !== undefined || rightToCancelDetails.rightToCancelCharge !== undefined;

    const offerSavePayload: ISaveOffer = {
        vin,
        menu: menuPayload,
        offer: offerPayload,
        dealer: dealerPayload,
        username,
        ...(userFullName && { userFullName }),
        ...(employees.length > 0 && { employees }),
        incentives,
        accessories,
        connectionId,
        calculationDetails,
        dealLifecycle,
        ...(!isAlternateScenario && { purpose }),
        ...(vehicleInsurance && { vehicleInsurance: vehicleInsurance }),
        ...(appliedTemplateId && { dealTemplateId: appliedTemplateId }),
        ...(mvDealStatePayload && { mvDealState: mvDealStatePayload }),
        ...(autoSelectStatePayload && { autoSelectState: autoSelectStatePayload }),
        ...(leaseResidualOverridePayload !== undefined && { leaseResidualOverride: leaseResidualOverridePayload }),
        ...(isMVVppOverrideIndicatorEnabled && darwinBaselinePayload && { darwinBaseline: darwinBaselinePayload }),
        ...(includeQualificationIds && { qualificationIds }),
        ...(baggageFuelEconomy && { baggageFuelEconomy }),
        rightToCancelDetails: hasRightToCancelData ? rightToCancelDetails : null,
        ...(shouldDeleteTestDrive && { testDrive: null }),
        ...(shouldDeleteVehicleFulfillment && { vehicleFulfillment: null }),
        ...(shouldReleaseReservation && { reservation: null }),
        tradeIns: tradesPayload
    };
    return offerSavePayload;
};

export const buildOfferSavePayloadObject = (state: StateTree, purpose = BuildPayloadPurpose.Unknown): ISaveOffer => {
    return buildMultipleOfferSavePayload(state, purpose);
};

export const buildApplyTemplateFromDealPayload = (state: StateTree, templateId: string): ISaveOffer | null => {
    const payload = buildOfferSavePayload(state);
    if (!payload) return null;
    return { ...payload, dealTemplateId: templateId };
};

export const buildTemplateSavePayload = (
    state: StateTree,
    purpose = BuildPayloadPurpose.Unknown,
    selectedScopeIds?: string[]
): Record<OfferType, ISaveOffer> => {
    const dealTypes: OfferType[] = [FINANCE, LEASE, CASH];
    return Object.fromEntries(
        dealTypes.map((dealType) => [dealType, buildMultipleOfferSavePayload(state, purpose, dealType, selectedScopeIds)])
    ) as Record<OfferType, ISaveOffer>;
};

const getPaymentWithoutSecondPaymentDueDate = (payloadPayment: GenericObject) => {
    let newPayload = structuredClone(payloadPayment);
    if (newPayload && 'secondPaymentDueDate' in newPayload) {
        newPayload = omit(newPayload, ['secondPaymentDueDate']);
    }
    return newPayload;
};
